import React, { useState } from 'react';
import { 
  ChartBarIcon,
  UsersIcon,
  ServerIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';
import Navbar from './Navbar';

const AdminAnalytics = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('overview');

  const overviewStats = [
    { 
      label: 'Total Users', 
      value: '2,847', 
      change: '+12%', 
      trend: 'up',
      icon: UsersIcon,
      color: 'text-blue-500'
    },
    { 
      label: 'Active Sessions', 
      value: '1,234', 
      change: '+8%', 
      trend: 'up',
      icon: EyeIcon,
      color: 'text-green-500'
    },
    { 
      label: 'API Requests', 
      value: '45.2K', 
      change: '+24%', 
      trend: 'up',
      icon: ServerIcon,
      color: 'text-purple-500'
    },
    { 
      label: 'Avg Response Time', 
      value: '245ms', 
      change: '-15ms', 
      trend: 'down',
      icon: ClockIcon,
      color: 'text-orange-500'
    }
  ];

  const performanceMetrics = [
    { name: 'CPU Usage', value: 45, max: 100, unit: '%', status: 'good' },
    { name: 'Memory Usage', value: 62, max: 100, unit: '%', status: 'warning' },
    { name: 'Disk Usage', value: 38, max: 100, unit: '%', status: 'good' },
    { name: 'Network I/O', value: 78, max: 100, unit: 'Mbps', status: 'good' }
  ];

  const topPages = [
    { path: '/dashboard', views: 12847, change: '+12%' },
    { path: '/ai-agents', views: 8934, change: '+8%' },
    { path: '/workflow-builder', views: 6721, change: '+15%' },
    { path: '/services', views: 5432, change: '+5%' },
    { path: '/logs', views: 3210, change: '-2%' }
  ];

  const userActivity = [
    { hour: '00:00', users: 120 },
    { hour: '04:00', users: 80 },
    { hour: '08:00', users: 450 },
    { hour: '12:00', users: 680 },
    { hour: '16:00', users: 520 },
    { hour: '20:00', users: 340 }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'critical': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getProgressWidth = (value, max) => {
    return Math.min((value / max) * 100, 100);
  };

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Analytics Dashboard</h1>
            <p className="text-gray-400 mt-2">Monitor system performance and user activity</p>
          </div>
          <div className="flex items-center gap-4">
            <select 
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </select>
            <button className="bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg font-medium transition-colors">
              Export Report
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-700 mb-8">
          <nav className="flex gap-8">
            {['overview', 'performance', 'users', 'content'].map((tab) => (
              <button
                key={tab}
                className={`pb-4 text-sm font-bold capitalize transition-colors ${
                  activeTab === tab
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-400 hover:text-white'
                }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {overviewStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <Icon className={`h-8 w-8 ${stat.color}`} />
                      <div className={`flex items-center gap-1 text-sm font-medium ${
                        stat.trend === 'up' ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {stat.trend === 'up' ? (
                          <ArrowTrendingUpIcon className="h-4 w-4" />
                        ) : (
                          <ArrowTrendingDownIcon className="h-4 w-4" />
                        )}
                        {stat.change}
                      </div>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">{stat.label}</p>
                      <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* User Activity Chart */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-white mb-6">User Activity (24h)</h3>
                <div className="space-y-4">
                  {userActivity.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-400 w-16">{item.hour}</span>
                      <div className="flex-1 mx-4">
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(item.users / 700) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-sm text-white font-medium w-12 text-right">{item.users}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Pages */}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-white mb-6">Top Pages</h3>
                <div className="space-y-4">
                  {topPages.map((page, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{page.path}</p>
                        <p className="text-sm text-gray-400">{page.views.toLocaleString()} views</p>
                      </div>
                      <div className={`text-sm font-medium ${
                        page.change.startsWith('+') ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {page.change}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="space-y-8">
            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {performanceMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">{metric.name}</h3>
                    <span className="text-2xl font-bold text-white">
                      {metric.value}{metric.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-300 ${getStatusColor(metric.status)}`}
                      style={{ width: `${getProgressWidth(metric.value, metric.max)}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-400 mt-2">
                    <span>0</span>
                    <span>{metric.max}{metric.unit}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* System Health */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-6">System Health Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="text-gray-400 text-sm">API Status</p>
                    <p className="text-white font-semibold">Operational</p>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="text-gray-400 text-sm">Database</p>
                    <p className="text-white font-semibold">Operational</p>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-700 rounded-lg">
                  <div>
                    <p className="text-gray-400 text-sm">AI Services</p>
                    <p className="text-white font-semibold">Operational</p>
                  </div>
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-white mb-4">User Growth</h3>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">+24%</p>
                  <p className="text-gray-400 text-sm">This month</p>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-white mb-4">Active Users</h3>
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-500">1,234</p>
                  <p className="text-gray-400 text-sm">Currently online</p>
                </div>
              </div>
              
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-bold text-white mb-4">Retention Rate</h3>
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-500">87%</p>
                  <p className="text-gray-400 text-sm">30-day retention</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Tab */}
        {activeTab === 'content' && (
          <div className="space-y-8">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-bold text-white mb-6">Content Performance</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-white font-medium mb-4">Most Viewed Content</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <span className="text-white">AI Agent Templates</span>
                      <span className="text-primary font-bold">2,847 views</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <span className="text-white">Workflow Examples</span>
                      <span className="text-primary font-bold">1,923 views</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <span className="text-white">API Documentation</span>
                      <span className="text-primary font-bold">1,456 views</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-white font-medium mb-4">Content Engagement</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <span className="text-white">Average Time on Page</span>
                      <span className="text-white font-bold">4:32</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <span className="text-white">Bounce Rate</span>
                      <span className="text-white font-bold">23%</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-700 rounded">
                      <span className="text-white">Pages per Session</span>
                      <span className="text-white font-bold">3.2</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;